import { differenceInSeconds } from 'date-fns'
import { Principal } from './principal'
import { StandardSecurityProfiles } from './security-profile'
import { Avatar } from './avatar'

/**
 * Account
 */
export class Account extends Principal {
  constructor (data = {}) {
    super(data)
    this.assign({
      ...data
    })
  }

  /**
   * Normalizes the assigned data
   */
  normalize () {
    super.normalize()
    this.isEnabled = Boolean(this.isEnabled)
    this.isVerified = Boolean(this.isVerified)
    this.avatars = this.castArray(this.avatars, Avatar)
  }

  /**
   * Returns account data cleared of any sensitive information
   * @param {Account} data Account to make safe
   * @returns {Account} Account without any sensitive data
   */
  static safe (data) {
    if (data) {
      const account = new Account(JSON.parse(JSON.stringify(data)))
      delete account.password
      delete account.failedLoginCount
      return account
    }
  }

  /**
   * Account avatars
   * @type {Array[Avatar]}
   */
  avatars

  /**
   * Returns the default avatar of the account,
   * or the first one if none is marked as default
   * @type {Avatar}
   */
  get avatar () {
    const { avatars = [] } = this
    return avatars.find(a => a.isDefault) || avatars[0]
  }

  /**
   * Full name, often overridden in descendants
   * to include things such as profile name etc.
   * @type {String}
   */
  get fullName () {
    return this.avatar?.fullName || this.name
  }

  /**
   * Checks whether account is assigned to the specified security profile
   * through one of its avatars
   * @param {String} name Security profile name
   * @returns {Boolean}
   */
  hasSecurityProfile (name) {
    return (this.avatars || []).some(a => a.hasSecurityProfile(name))
  }

  /**
  * Indicates whether the account is enabled
  * @type {Boolean}
  */
  isEnabled

  /**
  * Indicates whether the account has been legally verified
  * @type {Boolean}
  */
  isVerified

  /**
   * Public key of the account on the blockchain
   * @type {String}
   */
  publicKey

  /**
   * Password hash
   * @type {String}
   */
  password

  /**
   * Indicates a super-account
   * @type {Boolean}
   */
  get isSuperAccount () {
    return this.hasSecurityProfile(StandardSecurityProfiles.Super)
  }

  /**
   * Indicates a super-account, alias of {@link isSuperAccount}
   * @type {Boolean}
   */
  get isSuperAdministrator () {
    return this.isSuperAccount
  }

  /**
   * Indicates an internal integration account
   * @type {Boolean}
   */
  get isIntegrationAccount () {
    return this.hasSecurityProfile(StandardSecurityProfiles.Integration)
  }

  /**
   * Indicates an administrator account
   * @type {Boolean}
   */
  get isAdministrator () {
    return this.hasSecurityProfile(StandardSecurityProfiles.Super) ||
      this.hasSecurityProfile(StandardSecurityProfiles.Administrator)
  }

  /**
   * Indicates a regular user account
   * @type {Boolean}
   */
  get isUser () {
    return !(this.isSuperAccount || this.isIntegrationAccount || this.isAdministrator)
  }

  /**
   * Code of the country where account owner is located
   * ISO 3166 Alpha-2 country code
   * @type {String}
   */
  countryCode

  /**
   * Code of the preferred language
   * ISO 639-2 language code
   * @type {String}
   */
  languageCode

  /**
   * Name of country timezone, as per https://www.iana.org/time-zones
   * @type {String}
   */
  timezone

  /**
   * Origin IP address from which the account was registered
   * @type {String}
   */
  ip

  /**
  * Time of the most recent log in attempt
  * @type {Date}
  */
  lastLogin

  /**
   * Time in seconds of the last login attempt
   * @type {Number}
   */
  get lastLoginAge () {
    const { lastLogin } = this
    if (lastLogin) {
      return differenceInSeconds(Date.now(), lastLogin)
    }
  }

  /**
   * The number of subsequent failed login attempts
   * @type {Number}
   */
  failedLoginCount

  /**
   * Account permissions
   * @type {Dictionary<String, Boolean>}
   * @description The list contains only the explicitly granted or denied permissions.
   * The account might be granted other permissions by being assigned to one or more profiles,
   * each coming with its own set of permissions
   */
  allow

  /**
   * Account preferences
   * @type {Dictionary<String, any>}
   */
  preferences

  /**
   * Overrides serialization to prevent serializing of certain
   * runtime-only properties
   * @returns {Object}
   */
  toJSON () {
    const result = { ...this }
    return result
  }
}
